/**
 * AudioSnippetPlayer Component
 * ORCA360 - Voice Sample Playback for Speaker Role Assignment
 *
 * Renders a list of audio snippets per speaker with play/pause controls
 * and a clickable progress bar, using the shared useAudioSnippetPlayer hook.
 */

import { useRef } from 'react';
import { Play, Pause, Loader2, Headphones, VolumeX } from 'lucide-react';
import type { AudioPlaybackState } from '../hooks/useAudioSnippetPlayer';

export interface SpeakerSnippet {
  segment_id: string;
  start_ms: number;
  end_ms: number;
  start_time: string;
  end_time: string;
  text: string;
  duration_ms: number;
}

interface AudioSnippetPlayerProps {
  snippets: SpeakerSnippet[];
  speakerLabel: string;
  playbackState: AudioPlaybackState;
  onPlaySnippet: (snippetId: string, startMs: number, endMs: number) => void;
  onPause: () => void;
  onResume: () => void;
  onSeek: (fraction: number) => void;
  disabled?: boolean;
}

function formatSeconds(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatDurationLabel(ms: number): string {
  const sec = Math.round(ms / 1000);
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

export function AudioSnippetPlayer({
  snippets,
  speakerLabel,
  playbackState,
  onPlaySnippet,
  onPause,
  onResume,
  onSeek,
  disabled = false,
}: AudioSnippetPlayerProps) {
  const progressBarRef = useRef<HTMLDivElement>(null);

  if (disabled) {
    return (
      <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-gray-400 text-xs">
          <VolumeX size={14} />
          <span>No audio file available for voice playback</span>
        </div>
      </div>
    );
  }

  if (snippets.length === 0) {
    return (
      <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-gray-400 text-xs">
          <VolumeX size={14} />
          <span>No timed segments available for this speaker</span>
        </div>
      </div>
    );
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressBarRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onSeek(fraction);
  };

  return (
    <div className="mb-3 p-3 bg-ice rounded-lg border border-steel/15">
      <div className="flex items-center gap-1.5 mb-2">
        <Headphones size={13} className="text-navy/60" />
        <span className="text-[11px] font-semibold text-navy/60 uppercase tracking-wider">
          Voice Samples
        </span>
      </div>

      <div className="space-y-1.5">
        {snippets.map((snippet, idx) => {
          const isActive = playbackState.activeSnippetId === snippet.segment_id;
          const isPlaying = isActive && playbackState.isPlaying;
          const isLoading = isActive && playbackState.isLoading;
          const isPaused = isActive && !playbackState.isPlaying && playbackState.progress > 0 && playbackState.progress < 100;

          return (
            <div
              key={snippet.segment_id}
              className={`rounded-md transition-colors ${
                isActive
                  ? 'bg-orca-elevate/5 border-l-2 border-l-orca-elevate pl-2'
                  : 'pl-[10px]'
              }`}
            >
              {/* Snippet row */}
              <div className="flex items-center gap-2 py-1">
                {/* Index badge */}
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-navy/10 text-navy/60 text-[10px] font-bold flex items-center justify-center">
                  {idx + 1}
                </span>

                {/* Time range & duration */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-navy">
                      {snippet.start_time} – {snippet.end_time}
                    </span>
                    <span className="text-[10px] text-steel">
                      ({formatDurationLabel(snippet.duration_ms)})
                    </span>
                  </div>
                  <p className="text-[11px] text-steel italic truncate mt-0.5">
                    &ldquo;{snippet.text.substring(0, 100)}{snippet.text.length > 100 ? '...' : ''}&rdquo;
                  </p>
                </div>

                {/* Play/Pause button */}
                <button
                  onClick={() => {
                    if (isPlaying) {
                      onPause();
                    } else if (isPaused) {
                      onResume();
                    } else {
                      onPlaySnippet(snippet.segment_id, snippet.start_ms, snippet.end_ms);
                    }
                  }}
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    isPlaying
                      ? 'bg-orca-elevate text-white hover:bg-orca-elevate/80'
                      : isPaused
                      ? 'bg-orca-elevate/20 text-orca-elevate hover:bg-orca-elevate/30'
                      : 'bg-navy/10 text-navy/70 hover:bg-navy/20 hover:text-navy'
                  }`}
                  aria-label={
                    isPlaying
                      ? `Pause voice sample ${idx + 1} for ${speakerLabel}`
                      : `Play voice sample ${idx + 1} for ${speakerLabel}`
                  }
                >
                  {isLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : isPlaying ? (
                    <Pause size={14} />
                  ) : (
                    <Play size={14} className="ml-0.5" />
                  )}
                </button>
              </div>

              {/* Progress bar (only for active snippet) */}
              {isActive && (playbackState.progress > 0 || isPlaying || isPaused) && (
                <div className="pb-1.5 pl-7">
                  <div
                    ref={progressBarRef}
                    onClick={handleProgressClick}
                    className="h-1.5 bg-navy/10 rounded-full cursor-pointer group relative"
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(playbackState.progress)}
                    aria-label="Playback progress"
                  >
                    <div
                      className="h-full bg-orca-elevate rounded-full transition-[width] duration-100"
                      style={{ width: `${playbackState.progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-0.5">
                    <span className="font-mono text-[10px] text-steel">
                      {formatSeconds(playbackState.currentTime)}
                    </span>
                    <span className="font-mono text-[10px] text-steel">
                      {formatSeconds(playbackState.duration)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
